import { hasSuperUserAccess } from './sessionUser';
import { isGradeBeyondViewerAccess, isFirstDegreeOrHigher } from './gradeAccess';
import { collapseCourseDisplayName, normalizeStudentCoursesArray } from './coursesCatalog';

export function isStudentEnrolledInCourse(user, course) {
  if (!user || !course) return false;
  if (hasSuperUserAccess(user)) return true;
  const courseName = collapseCourseDisplayName(course.course_name);
  if (!courseName) return false;
  const target = courseName.toLowerCase();
  return normalizeStudentCoursesArray(user.courses).some(
    (c) => collapseCourseDisplayName(c).toLowerCase() === target
  );
}

export function filterCoursesForExerciseCreation(user, courses) {
  if (!Array.isArray(courses)) return [];
  if (hasSuperUserAccess(user)) return courses;
  return courses.filter((c) => isStudentEnrolledInCourse(user, c));
}

export function canCreateNonCourseExerciseOrType(user) {
  if (!user) return false;
  if (hasSuperUserAccess(user)) return true;
  return isFirstDegreeOrHigher(user);
}

export function validateExerciseTypeCreationAccess(user, { selectedCourseId, selectedGradeId, courses, grades }) {
  if (!user?.id) return 'You must be signed in to create an exercise type.';

  if (selectedCourseId) {
    const course = courses.find((c) => c.id === selectedCourseId);
    if (!course || !isStudentEnrolledInCourse(user, course)) {
      return 'You must be enrolled in this course to create exercise types for it.';
    }
  }

  if (selectedGradeId) {
    const grade = grades.find((g) => g.id === selectedGradeId);
    if (grade && isGradeBeyondViewerAccess(grade, user)) {
      return 'You can only create exercise types for grades up to one rank above your own.';
    }
  }

  if (!selectedCourseId && !selectedGradeId && !canCreateNonCourseExerciseOrType(user)) {
    return 'You must be at least 1st degree black belt to create standalone exercise types.';
  }

  return null;
}

export function validateExerciseCreationAccess(
  user,
  { typeSelectionSource, selectedCourseId, selectedGradeId, courses, grades }
) {
  if (!user?.id) return 'You must be signed in to create an exercise.';

  if (typeSelectionSource === 'course') {
    const course = courses.find((c) => c.id === selectedCourseId);
    if (!course || !isStudentEnrolledInCourse(user, course)) {
      return 'You must be enrolled in this course to create exercises for it.';
    }
    return null;
  }

  if (typeSelectionSource === 'grade') {
    const grade = grades.find((g) => g.id === selectedGradeId);
    if (grade && isGradeBeyondViewerAccess(grade, user)) {
      return 'You can only create exercises for grades up to one rank above your own.';
    }
    return null;
  }

  if (typeSelectionSource === 'nonCourse') {
    if (!canCreateNonCourseExerciseOrType(user)) {
      return 'You must be at least 1st degree black belt to create non-course exercises.';
    }
    return null;
  }

  return 'Please select a course, grade, or non-course exercise type.';
}
